import {
  useQuery,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import {
  DIRECTORY_STALE_TIME_SSE_MS,
  orgDirectoryListRefetchInterval,
  orgDirectoryStaleTime,
} from '../tenant-select/hooks/useOrgDirectoryRealtimeSync.js';
import {
  isClientAccessDenyKind,
  isEntitlementBlockKey,
  type ClientAccessDenyKind,
  type EntitlementDenialItem,
} from 'pi-kiosk-shared';
import { parseEntitlementDenialsFromApiErrorData } from '../../shared/api/tenantEntitlementDenied.js';
import type {
  CustomerEntitlementBlockKey,
  CustomerSurfaceEntitlementSnapshot,
} from './customerEntitlementGuards.js';
import { parseOptionalAccessRevision } from './parseAccessRevision.js';

/** Re-export — 403 ApiError.data `details.entitlementDenials` (Package F0). */
export { parseEntitlementDenialsFromApiErrorData };
export { parseOptionalAccessRevision };

interface CustomerSurfaceEntitlementApiResponse {
  readonly revision: number;
  readonly blocks: Readonly<Record<CustomerEntitlementBlockKey, boolean>>;
  readonly blockDenyKinds?: Readonly<
    Partial<Record<CustomerEntitlementBlockKey, ClientAccessDenyKind>>
  >;
  /** Package G — additive opaque invalidator; omit when absent. */
  readonly accessRevision?: unknown;
  /** Package G — ENFORCE commercial deny keys; omit when absent/empty. */
  readonly commercialDeniedBlockKeys?: unknown;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  return value as Record<string, unknown>;
}

/**
 * Parse wire `details.entitlementDenials` (403 body) into typed rows.
 * Drops malformed / unrecognized kinds; does not invent error codes.
 */
export function parseCustomerEntitlementDenials(
  details: unknown,
): readonly EntitlementDenialItem[] {
  const record = asRecord(details);
  if (record === null) {
    return [];
  }
  const raw = record.entitlementDenials;
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: EntitlementDenialItem[] = [];
  for (const item of raw) {
    const row = asRecord(item);
    if (row === null) {
      continue;
    }
    const blockKey = row.blockKey;
    const kind = row.kind;
    if (typeof blockKey !== 'string' || blockKey.trim().length === 0) {
      continue;
    }
    if (!isClientAccessDenyKind(kind)) {
      continue;
    }
    out.push({ blockKey, kind });
  }
  return out;
}

/**
 * Option A — keep only recognized ClientAccessDenyKind entries.
 * Missing / unknown kinds ⇒ omit (UNKNOWN presentation; never invent COMMERCIAL).
 */
export function parseCustomerBlockDenyKinds(
  raw: unknown,
): CustomerSurfaceEntitlementSnapshot['blockDenyKinds'] | undefined {
  if (raw === undefined || raw === null || typeof raw !== 'object') {
    return undefined;
  }
  const out: Partial<Record<CustomerEntitlementBlockKey, ClientAccessDenyKind>> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (isClientAccessDenyKind(value)) {
      out[key as CustomerEntitlementBlockKey] = value;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Package G — keep known entitlement block keys only; omit empty / malformed.
 * Never invent keys from SHADOW metadata (wire omits under OFF/SHADOW).
 */
export function parseCustomerCommercialDeniedBlockKeys(
  raw: unknown,
): readonly string[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item === 'string' && isEntitlementBlockKey(item)) {
      out.push(item);
    }
  }
  return out.length > 0 ? out : undefined;
}

/**
 * True when surface entitlements can be fetched for the browse chrome.
 * Identity-only hub (`__platform__` + jwt null) has no API tenant — query stays idle
 * (BAN inventing home jwt). Route guards must allow platform-safe hubs separately.
 */
export function isCustomerSurfaceEntitlementQueryEnabled(tenantCode: string): boolean {
  return !isPlatformChromeTenant(tenantCode);
}

export function isIdentityOnlyEntitlementIdle(tenantCode: string): boolean {
  return isPlatformChromeTenant(tenantCode);
}

/** G12 — /orgs-class staleTime + poll + tab-return parity when directory SSE is wired. */
export type SurfaceEntitlementRealtimeOptions = {
  readonly directorySseConnected: boolean;
};

export function surfaceEntitlementQueryOptions(
  sseConnected: boolean,
): {
  staleTime: number;
  refetchInterval: number | false;
  refetchOnWindowFocus: true;
} {
  return {
    staleTime: orgDirectoryStaleTime(sseConnected),
    refetchInterval: orgDirectoryListRefetchInterval(sseConnected),
    refetchOnWindowFocus: true,
  };
}

export { DIRECTORY_STALE_TIME_SSE_MS };

/**
 * F-AC-31 — invalidate tenant surface-entitlement snapshot after race/stale deny
 * so RestockAlertCheckbox (and peers) resync local paid-gate state.
 */
export function invalidateCustomerSurfaceEntitlementQuery(
  queryClient: QueryClient,
  tenantCode: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: customerQueryKeys.surfaceEntitlement(tenantCode),
  });
}

/**
 * Surface entitlements for the API tenant (URL org, or home jwt under platform chrome).
 * Platform chrome without jwt stays idle — never hit `/api/__platform__/`.
 */
export function useCustomerSurfaceEntitlementQuery(
  realtime?: SurfaceEntitlementRealtimeOptions,
): UseQueryResult<CustomerSurfaceEntitlementSnapshot, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  // Key by remapped home jwt under `__platform__` so cache matches real-tenant browse.
  const entitlementTenantCode = tenantCode;

  return useQuery({
    queryKey: customerQueryKeys.surfaceEntitlement(entitlementTenantCode),
    queryFn: async (): Promise<CustomerSurfaceEntitlementSnapshot> => {
      const data = await api.request<CustomerSurfaceEntitlementApiResponse>(
        '/customer/surface-entitlement',
      );
      if (data.blocks === undefined || data.blocks === null) {
        throw new Error(
          'Surface entitlement response missing blocks. Retry or reopen the organization.',
        );
      }
      const blockDenyKinds = parseCustomerBlockDenyKinds(data.blockDenyKinds);
      const accessRevision = parseOptionalAccessRevision(data.accessRevision);
      const commercialDeniedBlockKeys = parseCustomerCommercialDeniedBlockKeys(
        data.commercialDeniedBlockKeys,
      );
      return {
        revision: data.revision,
        blocks: data.blocks,
        ...(blockDenyKinds !== undefined ? { blockDenyKinds } : {}),
        ...(accessRevision !== undefined ? { accessRevision } : {}),
        ...(commercialDeniedBlockKeys !== undefined
          ? { commercialDeniedBlockKeys }
          : {}),
      };
    },
    enabled: isCustomerSurfaceEntitlementQueryEnabled(tenantCode),
    ...(realtime !== undefined
      ? surfaceEntitlementQueryOptions(realtime.directorySseConnected)
      : { staleTime: 60_000 }),
    retry: 1,
  });
}
